/**
 * Revenue types — mirror of backend/src/models/Revenue.ts. Keep in sync.
 *
 * No approval workflow: creating a revenue is a one-shot post to the ledger.
 * Reversing it soft-deletes the row and posts a compensating OUT entry.
 */

export type RevenueMethod = 'cash' | 'bank_transfer' | 'cheque' | 'online';
export const REVENUE_METHODS: RevenueMethod[] = ['cash', 'bank_transfer', 'cheque', 'online'];

export type RevenueCounterpartyKind =
  | 'student' | 'parent' | 'employee' | 'vendor' | 'donor' | 'other';

export const REVENUE_COUNTERPARTY_KINDS: RevenueCounterpartyKind[] =
  ['student', 'parent', 'employee', 'vendor', 'donor', 'other'];

export const REVENUE_COUNTERPARTY_KIND_LABELS: Record<RevenueCounterpartyKind, string> = {
  student: 'Student',
  parent: 'Parent',
  employee: 'Employee',
  vendor: 'Vendor',
  donor: 'Donor',
  other: 'Other',
};

export interface RevenueCounterparty {
  kind: RevenueCounterpartyKind;
  id?: string;
  name: string;
}

export interface RevenueCategory {
  _id: string;
  name: string;
  description?: string;
  isActive: boolean;
  /** Ledger account chosen for it; null uses `suggestedAccountCode`. */
  glAccountCode?: string | null;
  suggestedAccountCode?: string;
}

export interface RevenueSubCategory {
  _id: string;
  categoryId: string | { _id: string; name: string };
  name: string;
  description?: string;
  isActive: boolean;
}

export interface Revenue {
  _id: string;
  schoolId: string | { _id: string; name: string };
  title: string;
  amount: number;
  date: string;
  categoryId?: { _id: string; name: string };
  subCategoryId?: { _id: string; name: string };
  notes?: string;
  method: RevenueMethod;
  referenceNumber?: string;
  counterparty?: RevenueCounterparty;
  documentFilename?: string;
  documentName?: string;
  documentType?: 'image' | 'pdf';
  createdBy: { _id: string; name: string };
  externalReference?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RevenueStats {
  total: { amount: number; count: number };
}
